import { Box, Grid, Link, Stack, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router'
import { WaterQualityIndexSelectors } from '../../../../store/waterQualityIndex'

const SEGMENT_ORDER = ['unsafe', 'cautious', 'safe', 'notScored'] as const

export const TestResultsCard = () => {
  const theme = useTheme()
  const counts = useSelector(WaterQualityIndexSelectors.selectTestResultCounts)

  const colors: Record<(typeof SEGMENT_ORDER)[number], string> = {
    unsafe: theme.palette.error.main,
    cautious: theme.palette.warning.main,
    safe: theme.palette.success.main,
    notScored: theme.palette.grey[400],
  }

  const gradient = (() => {
    if (counts.total === 0) {
      return `conic-gradient(from -90deg, ${theme.palette.grey[300]} 0deg 360deg)`
    }
    let acc = 0
    const stops = SEGMENT_ORDER.map((key) => {
      const value = counts[key]
      const start = (acc / counts.total) * 360
      acc += value
      const end = (acc / counts.total) * 360
      return `${colors[key]} ${start}deg ${end}deg`
    })
    return `conic-gradient(from -90deg, ${stops.join(', ')})`
  })()

  const LegendItem = ({
    color,
    label,
    value,
  }: {
    color: string
    label: string
    value: number
  }) => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  )

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        TEST RESULTS
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: gradient,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 90,
              height: 90,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              {counts.total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              readings
            </Typography>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={1}>
        <Grid size={{ xs: 6 }}>
          <LegendItem color={colors.unsafe} label="Unsafe" value={counts.unsafe} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <LegendItem color={colors.cautious} label="Cautious" value={counts.cautious} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <LegendItem color={colors.safe} label="Safe" value={counts.safe} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <LegendItem color={colors.notScored} label="Not Scored" value={counts.notScored} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 1.5 }}>
        <Link
          component={RouterLink}
          to="/profile/results"
          variant="caption"
          sx={{ color: 'primary.dark', textDecoration: 'none', fontWeight: 600 }}
        >
          View test results →
        </Link>
      </Box>
    </Box>
  )
}
